"use client";

import React, { useEffect, useRef, useState } from "react";
import { Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function CaringForTheJourney() {
  const textReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="102874" id="a-gentle-approach-to-education" className="bg-background py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={imageReveal.ref} className={`lg:col-span-5 relative flex flex-col items-center transition-all duration-1000 ease-out ${imageReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            {}
            <div className="w-[85%] self-start relative z-10">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559791/img/pda_child_nature.png" alt="Supportive learning environment in Australia" className="w-full aspect-[4/3] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl shadow-lg" />
            </div>

            {}
            <div className="w-[65%] relative z-20 -mt-16 self-end mr-4">
              <div className="aspect-square rounded-full p-2 bg-background shadow-2xl">
                <img src="https://pdaaustralia.com.au/wp-content/uploads/2026/03/pda-child-nature.png" alt="Native Australian flora representing growth" className="w-full h-full object-cover rounded-full" />
              </div>
            </div>

            {}
            <div className="w-[80%] self-start relative z-10 -mt-12 ml-8">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559791/img/inclusive_classroom_australia.png" alt="Inclusive Australian classroom setting" className="w-full aspect-[16/10] object-cover rounded-bl-[3rem] rounded-tr-[3rem] rounded-tl-xl rounded-br-xl shadow-lg" />
            </div>
          </div>

          {}
          <div ref={textReveal.ref} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${textReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Leaf className="w-10 h-10 text-chart-3 mb-8 opacity-90" strokeWidth={1.2} aria-hidden="true" />
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary tracking-tight mb-10 leading-tight">
              A Gentle Approach to Education
            </h2>
            
            <div className="space-y-6 text-xl text-foreground font-sans leading-relaxed max-w-2xl">
              <p>
                PDA Australia is dedicated to reframing the understanding of Pathological Demand Avoidance through a lens of compassion and neuro-affirming practice. We believe in creating a warm sanctuary where every child feels truly seen and supported.
              </p>
              <p>
                By offering evidence-based resources that prioritize autonomy and emotional well-being, we help Australian families and teachers move beyond traditional compliance models. Our goal is to ensure educational environments authentically reflect every child’s unique needs.
              </p>
              <p>
                With a focus on building a trusted community of advocates, we provide the tools necessary to navigate the NDIS and school systems. By emphasizing dignity and understanding, we are growing into the leading resource for PDA families across Australia.
              </p>
            </div>

            <div className="mt-12">
              <Button asChild variant="outline" size="lg" className="rounded-full px-10 font-serif text-lg tracking-wide bg-chart-2 text-primary-foreground border-none hover:opacity-90 transition-opacity shadow-md">
                <Link to="https://pdaaustralia.com.au/resources/">
                  Explore Our Support Resources
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}